import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, Code, Copy, Check, Plus, Trash2, ExternalLink,
  Loader2, Globe, FileText, BarChart3, Clock, Shield, Eye, Save, Palette, Star, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { useBrandName } from '@/lib/branding';

const SNIPPET_TABS = [
  { key: 'iframe', label: 'iFrame', icon: Globe },
  { key: 'javascript', label: 'JavaScript', icon: Code },
  { key: 'react', label: 'React', icon: FileText },
];

function CopyBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  return (
    <div className="relative group">
      <pre className="p-4 rounded-xl bg-[#0d1117] border border-border/30 text-[11px] font-mono text-emerald-300 overflow-auto max-h-64 whitespace-pre-wrap leading-relaxed">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-lg bg-muted/30 border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all opacity-0 group-hover:opacity-100"
        data-testid={`copy-${language}`}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function EmbedManagerPage() {
  const APP = useBrandName();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [embeds, setEmbeds] = useState([]);
  const [usageStats, setUsageStats] = useState({}); // keyed by embed_id
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [editingEmbedId, setEditingEmbedId] = useState(null);
  const [codeLoadingId, setCodeLoadingId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [listFilter, setListFilter] = useState('all'); // all | active | expired
  const [revokingExpired, setRevokingExpired] = useState(false);

  // New embed form
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    resource_type: 'report',
    resource_id: '',
    width: '100%',
    height: '600px',
    theme: 'dark',
    brand_accent: '#3b82f6',
    brand_background: '#0b1220',
    brand_foreground: '#e2e8f0',
    label: '',
  });

  // Generated snippet view
  const [snippetData, setSnippetData] = useState(null);
  const [snippetTab, setSnippetTab] = useState('iframe');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  // Brand theme presets — tenant-scoped named palettes so teams can
  // reuse their colour system without re-picking hex codes every embed.
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  const refreshPresets = React.useCallback(() => {
    api.listBrandPresets()
      .then((p) => setPresets(Array.isArray(p) ? p : []))
      .catch(() => setPresets([]));
  }, []);

  const refreshUsage = React.useCallback(() => {
    api.getEmbedUsageSummary()
      .then((r) => {
        const map = {};
        (r?.stats || []).forEach((s) => { map[s.embed_id] = s; });
        setUsageStats(map);
      })
      .catch(() => setUsageStats({}));
  }, []);

  useEffect(() => {
    Promise.all([
      api.getReports().then(r => setReports(r || [])).catch(() => []),
      api.listEmbeds().then(e => setEmbeds(e || [])).catch(() => []),
    ]).finally(() => setLoading(false));
    refreshUsage();
    refreshPresets();
  }, [refreshUsage, refreshPresets]);

  const applyPreset = (presetId) => {
    const p = presets.find(x => x.preset_id === presetId);
    if (!p) return;
    setForm(prev => ({
      ...prev,
      brand_accent: p.brand_theme?.accent || prev.brand_accent,
      brand_background: p.brand_theme?.background || prev.brand_background,
      brand_foreground: p.brand_theme?.foreground || prev.brand_foreground,
    }));
    toast.success(`Applied preset "${p.name}"`);
  };

  const handleSavePreset = async () => {
    const name = newPresetName.trim();
    if (!name) { toast.error('Preset name required'); return; }
    setSavingPreset(true);
    try {
      const created = await api.createBrandPreset({
        name,
        brand_theme: {
          accent: form.brand_accent,
          background: form.brand_background,
          foreground: form.brand_foreground,
        },
      });
      setPresets(prev => [created, ...prev]);
      setNewPresetName('');
      toast.success(`Preset "${name}" saved`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await api.deleteBrandPreset(presetId);
      setPresets(prev => prev.filter(p => p.preset_id !== presetId));
      toast.success('Preset removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleDefault = async (preset) => {
    try {
      if (preset.is_default) {
        await api.clearDefaultBrandPreset();
        setPresets(prev => prev.map(p => ({ ...p, is_default: false })));
        toast.success(`"${preset.name}" is no longer the tenant default`);
      } else {
        await api.setDefaultBrandPreset(preset.preset_id);
        setPresets(prev => prev.map(p => ({ ...p, is_default: p.preset_id === preset.preset_id })));
        toast.success(`"${preset.name}" is now the tenant default palette`);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Auto-apply the tenant default preset the first time a user opens the
  // Brand section, unless they've already tweaked the palette. Prevents
  // stomping on custom colors mid-session.
  const [brandAutoAppliedForPreset, setBrandAutoAppliedForPreset] = useState(null);
  useEffect(() => {
    if (form.theme !== 'brand') return;
    if (!presets.length) return;
    const def = presets.find(p => p.is_default);
    if (!def) return;
    if (brandAutoAppliedForPreset === def.preset_id) return;
    setForm(prev => ({
      ...prev,
      brand_accent: def.brand_theme?.accent || prev.brand_accent,
      brand_background: def.brand_theme?.background || prev.brand_background,
      brand_foreground: def.brand_theme?.foreground || prev.brand_foreground,
    }));
    setBrandAutoAppliedForPreset(def.preset_id);
  }, [form.theme, presets, brandAutoAppliedForPreset]);

  const resetForm = () => setForm({
    resource_type: 'report', resource_id: '', width: '100%', height: '600px',
    theme: 'dark', brand_accent: '#3b82f6', brand_background: '#0b1220',
    brand_foreground: '#e2e8f0', label: '',
  });

  const openCreate = () => {
    setEditingEmbedId(null);
    resetForm();
    setSnippetData(null);
    setCreateOpen(true);
  };

  const handleEditEmbed = (embed) => {
    setEditingEmbedId(embed.embed_id);
    setForm({
      resource_type: embed.resource_type || 'report',
      resource_id: embed.resource_id || '',
      width: embed.width || '100%',
      height: embed.height || '600px',
      theme: embed.theme || 'dark',
      brand_accent: embed.brand_theme?.accent || '#3b82f6',
      brand_background: embed.brand_theme?.background || '#0b1220',
      brand_foreground: embed.brand_theme?.foreground || '#e2e8f0',
      label: embed.label || '',
    });
    setCreateOpen(true);
  };

  const buildBrandPayload = () => ({
    accent: form.brand_accent,
    background: form.brand_background,
    foreground: form.brand_foreground,
  });

  const handleGenerate = async () => {
    if (!form.resource_id) { toast.error('Select a report or dashboard'); return; }
    setGenerating(true);
    try {
      if (editingEmbedId) {
        // Edit existing embed — update presentation, token/resource unchanged.
        const payload = {
          label: form.label,
          theme: form.theme,
          width: form.width,
          height: form.height,
        };
        if (form.theme === 'brand') payload.brand_theme = buildBrandPayload();
        await api.updateEmbed(editingEmbedId, payload);
        const fresh = await api.getEmbedSnippet(editingEmbedId);
        setSnippetData(fresh);
        setCreateOpen(false);
        setEditingEmbedId(null);
        api.listEmbeds().then(e => setEmbeds(e || [])).catch(() => {});
        toast.success('Embed updated');
        return;
      }
      const payload = {
        resource_type: form.resource_type,
        resource_id: form.resource_id,
        width: form.width,
        height: form.height,
        theme: form.theme,
        label: form.label,
        public_origin: window.location.origin,
      };
      if (form.theme === 'brand') {
        payload.brand_theme = buildBrandPayload();
      }
      const result = await api.generateEmbedSnippet(payload);
      setSnippetData(result);
      setCreateOpen(false);
      // Refresh embeds list + usage
      api.listEmbeds().then(e => setEmbeds(e || [])).catch(() => {});
      refreshUsage();
      toast.success('Embed snippet generated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleShowCode = async (embed) => {
    setCodeLoadingId(embed.embed_id);
    try {
      const data = await api.getEmbedSnippet(embed.embed_id);
      setSnippetData(data);
    } catch (err) {
      toast.error(err.message || 'Could not load embed code');
    } finally {
      setCodeLoadingId(null);
    }
  };

  const handlePreview = async (embed) => {
    setPreviewLoadingId(embed.embed_id);
    try {
      const data = await api.getEmbedSnippet(embed.embed_id);
      const url = `${window.location.origin}/embed/${embed.resource_type}/${embed.resource_id}?token=${encodeURIComponent(data.embed_token)}`;
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.message || 'Could not load preview');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleRevoke = async (embedId) => {
    setRevoking(embedId);
    try {
      await api.revokeEmbed(embedId);
      setEmbeds(prev => prev.filter(e => e.embed_id !== embedId));
      toast.success('Embed token revoked');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(null);
    }
  };

  const getResourceName = (embed) => {
    const r = reports.find(rp => rp.report_id === embed.resource_id);
    return r?.name || embed.resource_id;
  };

  const isExpired = (embed) => {
    if (!embed.expires_at) return false;
    return new Date(embed.expires_at) < new Date();
  };

  const expiredCount = embeds.filter(isExpired).length;
  const activeCount = embeds.length - expiredCount;

  // Filter by status, then surface stale links first (soonest expiry on top).
  const visibleEmbeds = embeds
    .filter((e) => listFilter === 'all' || (listFilter === 'expired' ? isExpired(e) : !isExpired(e)))
    .sort((a, b) => {
      const ae = isExpired(a), be = isExpired(b);
      if (ae !== be) return ae ? -1 : 1;
      const at = a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
      const bt = b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
      return at - bt;
    });

  const handleRevokeExpired = async () => {
    const stale = embeds.filter(isExpired);
    if (!stale.length) return;
    if (!window.confirm(`Revoke ${stale.length} expired embed${stale.length > 1 ? 's' : ''}? This permanently disables ${stale.length > 1 ? 'their' : 'its'} link${stale.length > 1 ? 's' : ''}.`)) return;
    setRevokingExpired(true);
    try {
      const ids = stale.map((e) => e.embed_id);
      await Promise.all(ids.map((id) => api.revokeEmbed(id).catch(() => null)));
      setEmbeds((prev) => prev.filter((e) => !ids.includes(e.embed_id)));
      toast.success(`Revoked ${ids.length} expired embed${ids.length > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.message || 'Failed to revoke expired embeds');
    } finally {
      setRevokingExpired(false);
    }
  };

  const FILTERS = [
    { key: 'all', label: 'All', count: embeds.length },
    { key: 'active', label: 'Active', count: activeCount },
    { key: 'expired', label: 'Expired', count: expiredCount },
  ];

  return (
    <div className="h-full flex flex-col" data-testid="embed-manager-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50 flex items-center gap-4" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-lg w-8 h-8" data-testid="embed-manager-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Embedded Analytics
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Generate embed snippets to integrate {APP} dashboards into external apps</p>
        </div>
        <Button onClick={openCreate} className="rounded-lg gap-1.5" data-testid="create-embed-btn">
          <Plus className="w-4 h-4" /> New Embed
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Embeds List */}
        <div className="w-96 border-r border-border/50 flex flex-col" style={{ background: 'var(--glass-bg)' }}>
          <div className="p-4 border-b border-border/30 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                Embeds ({visibleEmbeds.length})
              </p>
              {expiredCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRevokeExpired}
                  disabled={revokingExpired}
                  className="h-6 text-[9px] text-destructive hover:bg-destructive/10 rounded gap-1"
                  data-testid="revoke-expired-btn"
                >
                  {revokingExpired ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Revoke expired ({expiredCount})
                </Button>
              )}
            </div>
            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/20 border border-border/30" data-testid="embed-status-filter">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setListFilter(f.key)}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${listFilter === f.key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`embed-filter-${f.key}`}
                >
                  {f.label}
                  <span className={`px-1 rounded-full text-[8px] ${f.key === 'expired' && f.count > 0 ? 'bg-red-500/15 text-red-400' : 'bg-muted/40 text-muted-foreground'}`}>{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            ) : embeds.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Code className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No embeds yet</p>
                <p className="text-xs text-muted-foreground/80 mt-1">Create your first embed to get started</p>
              </div>
            ) : visibleEmbeds.length === 0 ? (
              <div className="text-center py-12 px-4" data-testid="embed-filter-empty">
                <Code className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No {listFilter} embeds</p>
                <p className="text-xs text-muted-foreground/80 mt-1">Try a different filter above</p>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {visibleEmbeds.map((embed, idx) => (
                  <div
                    key={embed.embed_id || `embed-${idx}`}
                    className={`glass-card rounded-xl p-3 border transition-all cursor-pointer hover:border-primary/30 ${snippetData?.embed_id === embed.embed_id ? 'border-primary/40 bg-primary/5' : 'border-border/30'}`}
                    onClick={() => {
                      setSnippetData(null);
                      // We don't have the snippet for existing embeds; show basic info
                    }}
                    data-testid={`embed-card-${embed.embed_id}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-primary/10 border border-primary/20">
                        {embed.resource_type === 'dashboard' ? <BarChart3 className="w-3.5 h-3.5 text-primary" /> : <FileText className="w-3.5 h-3.5 text-primary" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-foreground truncate">{embed.label || getResourceName(embed)}</p>
                        <p className="text-[9px] text-muted-foreground font-mono">{embed.embed_id}</p>
                      </div>
                      <Badge className={`text-[8px] ${isExpired(embed) ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'}`}>
                        {isExpired(embed) ? 'Expired' : 'Active'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[9px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{(embed.permissions || ['read']).join(', ')}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{embed.expires_at ? new Date(embed.expires_at).toLocaleDateString() : 'No expiry'}</span>
                      {embed.theme && (
                        <span className="flex items-center gap-1 uppercase tracking-wider" data-testid={`embed-theme-tag-${embed.embed_id}`}>
                          <span className="w-2 h-2 rounded-full" style={{ background: embed.theme === 'brand' ? (embed.brand_theme?.accent || '#3b82f6') : (embed.theme === 'light' ? '#ffffff' : '#1e293b'), border: '1px solid rgba(255,255,255,0.15)' }} />
                          {embed.theme}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid={`embed-usage-${embed.embed_id}`}>
                      <div className="rounded-md bg-muted/20 border border-border/20 px-2 py-1.5">
                        <p className="text-[8px] uppercase tracking-wider text-muted-foreground">Views</p>
                        <p className="text-[13px] font-semibold text-foreground font-mono" data-testid={`embed-views-${embed.embed_id}`}>
                          {(usageStats[embed.embed_id]?.views ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-md bg-muted/20 border border-border/20 px-2 py-1.5">
                        <p className="text-[8px] uppercase tracking-wider text-muted-foreground">Unique viewers</p>
                        <p className="text-[13px] font-semibold text-foreground font-mono" data-testid={`embed-uniques-${embed.embed_id}`}>
                          {(usageStats[embed.embed_id]?.unique_viewers ?? 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handlePreview(embed); }}
                        disabled={previewLoadingId === embed.embed_id}
                        className="h-6 text-[9px] text-muted-foreground hover:text-primary rounded"
                        data-testid={`preview-embed-${embed.embed_id}`}
                      >
                        {previewLoadingId === embed.embed_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />} Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleShowCode(embed); }}
                        disabled={codeLoadingId === embed.embed_id}
                        className="h-6 text-[9px] text-muted-foreground hover:text-primary rounded"
                        data-testid={`code-embed-${embed.embed_id}`}
                      >
                        {codeLoadingId === embed.embed_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Code className="w-3 h-3 mr-1" />} Code
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEditEmbed(embed); }}
                        className="h-6 text-[9px] text-muted-foreground hover:text-primary rounded"
                        data-testid={`edit-embed-${embed.embed_id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleRevoke(embed.embed_id); }}
                        disabled={revoking === embed.embed_id}
                        className="h-6 text-[9px] text-destructive hover:bg-destructive/10 rounded ml-auto"
                        data-testid={`revoke-embed-${embed.embed_id}`}
                      >
                        {revoking === embed.embed_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right: Snippet Display */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {snippetData ? (
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-heading font-semibold text-foreground">Embed Ready</h2>
                    <p className="text-xs text-muted-foreground">Copy and paste one of these snippets into your application</p>
                  </div>
                </div>

                {/* Embed Info */}
                <div className="glass-card rounded-xl p-4 border border-border/30 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-[11px]">
                    <div>
                      <p className="text-muted-foreground mb-0.5">Embed ID</p>
                      <p className="font-mono text-foreground">{snippetData.embed_id}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-0.5">Token</p>
                      <p className="font-mono text-foreground truncate">{snippetData.embed_token?.slice(0, 20)}...</p>
                    </div>
                  </div>
                  <div className="text-[11px]">
                    <p className="text-muted-foreground mb-0.5">Embed URL</p>
                    <div className="flex items-start gap-2">
                      <p className="font-mono text-primary break-all flex-1 leading-relaxed" data-testid="embed-url-full">{snippetData.embed_url}</p>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard?.writeText(snippetData.embed_url || ''); toast.success('Embed URL copied'); }}
                        className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        data-testid="embed-url-copy"
                        title="Copy embed URL"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  </div>
                </div>

                {/* Snippet Tabs */}
                <div>
                  <div className="flex gap-1 mb-3">
                    {SNIPPET_TABS.map(tab => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.key}
                          onClick={() => setSnippetTab(tab.key)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${snippetTab === tab.key ? 'bg-primary/15 text-primary border border-primary/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                          data-testid={`snippet-tab-${tab.key}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                  <CopyBlock
                    code={snippetData.snippets?.[snippetTab] || 'Snippet not available'}
                    language={snippetTab}
                  />
                </div>

                {/* Usage Notes */}
                <div className="glass-card rounded-xl p-4 border border-border/30 space-y-2">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Integration Notes</p>
                  <ul className="space-y-1.5 text-[11px] text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      The embed token is valid for 1 year. Revoke it anytime from this page.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      The iframe auto-resizes when the embedded content sends resize events.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      For React apps, install the component as shown and pass your desired dimensions.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      Embedded views are read-only and do not require user authentication.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm">
                <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 w-fit mx-auto mb-4">
                  <Code className="w-10 h-10 text-primary/40" />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground mb-1">Embed {APP} Anywhere</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Generate secure embed snippets to integrate your dashboards and reports into any website, internal tool, or customer-facing portal.
                </p>
                <Button onClick={openCreate} className="mt-4 rounded-lg gap-1.5" data-testid="create-embed-cta">
                  <Plus className="w-4 h-4" /> Create Embed
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Embed Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setEditingEmbedId(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="create-embed-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" /> {editingEmbedId ? 'Edit Embed' : 'Generate Embed Snippet'}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{editingEmbedId ? 'Update this embed\u2019s appearance. The token and resource stay the same, so snippets already in use keep working.' : 'Configure and generate an embed snippet for your report or dashboard.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">Resource Type</Label>
              <Select value={form.resource_type} onValueChange={v => setForm(p => ({ ...p, resource_type: v, resource_id: '' }))} disabled={!!editingEmbedId}>
                <SelectTrigger className="mt-1 rounded-lg bg-muted/30 border-border/30" data-testid="embed-resource-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="report">Report / Dashboard</SelectItem>
                  <SelectItem value="dashboard">Legacy Dashboard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Select Report</Label>
              <Select value={form.resource_id} onValueChange={v => setForm(p => ({ ...p, resource_id: v }))} disabled={!!editingEmbedId}>
                <SelectTrigger className="mt-1 rounded-lg bg-muted/30 border-border/30" data-testid="embed-resource-id">
                  <SelectValue placeholder="Choose a report..." />
                </SelectTrigger>
                <SelectContent>
                  {reports.map(r => (
                    <SelectItem key={r.report_id} value={r.report_id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Label (optional)</Label>
              <Input
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                placeholder="e.g. Customer Portal Dashboard"
                className="mt-1 rounded-lg bg-muted/30 border-border/30 text-foreground"
                data-testid="embed-label"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Width</Label>
                <Input
                  value={form.width}
                  onChange={e => setForm(p => ({ ...p, width: e.target.value }))}
                  className="mt-1 rounded-lg bg-muted/30 border-border/30 text-foreground font-mono text-[11px]"
                  data-testid="embed-width"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Height</Label>
                <Input
                  value={form.height}
                  onChange={e => setForm(p => ({ ...p, height: e.target.value }))}
                  className="mt-1 rounded-lg bg-muted/30 border-border/30 text-foreground font-mono text-[11px]"
                  data-testid="embed-height"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Theme</Label>
              <Select value={form.theme} onValueChange={v => setForm(p => ({ ...p, theme: v }))}>
                <SelectTrigger className="mt-1 rounded-lg bg-muted/30 border-border/30" data-testid="embed-theme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="brand">Brand (custom palette)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.theme === 'brand' && (
              <div className="space-y-3" data-testid="embed-brand-theme-inputs">
                {presets.length > 0 && (
                  <div className="space-y-1.5" data-testid="embed-brand-preset-picker">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Palette className="w-3 h-3" /> Load from preset
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {presets.map(p => (
                        <div key={p.preset_id} className={`group inline-flex items-center rounded-lg border pl-1.5 py-0.5 ${p.is_default ? 'border-amber-500/40 bg-amber-500/10' : 'border-border/40 bg-muted/20'}`} data-testid={`embed-brand-preset-${p.preset_id}`}>
                          <button
                            type="button"
                            onClick={() => handleToggleDefault(p)}
                            className={`p-1 transition ${p.is_default ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400 opacity-60 hover:opacity-100'}`}
                            aria-label={p.is_default ? `Unset ${p.name} as tenant default` : `Set ${p.name} as tenant default`}
                            title={p.is_default ? 'Tenant default (click to unset)' : 'Set as tenant default'}
                            data-testid={`embed-brand-preset-default-${p.preset_id}`}
                          >
                            <Star className={`w-3 h-3 ${p.is_default ? 'fill-amber-400' : ''}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => applyPreset(p.preset_id)}
                            className="flex items-center gap-1.5 px-1 py-1 text-[10px] text-foreground/90 hover:text-primary"
                            data-testid={`embed-brand-preset-apply-${p.preset_id}`}
                          >
                            <span className="flex gap-0.5">
                              <span className="w-2 h-2 rounded-sm" style={{ background: p.brand_theme?.accent || '#3b82f6' }} />
                              <span className="w-2 h-2 rounded-sm" style={{ background: p.brand_theme?.background || '#0b1220' }} />
                              <span className="w-2 h-2 rounded-sm" style={{ background: p.brand_theme?.foreground || '#e2e8f0' }} />
                            </span>
                            <span className="truncate max-w-[110px]">{p.name}</span>
                            {p.is_default && <Badge className="text-[8px] bg-amber-500/20 text-amber-400 border-amber-500/30 h-4 px-1">Default</Badge>}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePreset(p.preset_id)}
                            className="opacity-0 group-hover:opacity-100 transition p-1 text-muted-foreground hover:text-destructive"
                            aria-label={`Delete preset ${p.name}`}
                            data-testid={`embed-brand-preset-delete-${p.preset_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Accent</Label>
                    <Input
                      type="color"
                      value={form.brand_accent}
                      onChange={e => setForm(p => ({ ...p, brand_accent: e.target.value }))}
                      className="mt-1 h-9 rounded-lg p-1 bg-muted/30 border-border/30"
                      data-testid="embed-brand-accent"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Background</Label>
                    <Input
                      type="color"
                      value={form.brand_background}
                      onChange={e => setForm(p => ({ ...p, brand_background: e.target.value }))}
                      className="mt-1 h-9 rounded-lg p-1 bg-muted/30 border-border/30"
                      data-testid="embed-brand-background"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Foreground</Label>
                    <Input
                      type="color"
                      value={form.brand_foreground}
                      onChange={e => setForm(p => ({ ...p, brand_foreground: e.target.value }))}
                      className="mt-1 h-9 rounded-lg p-1 bg-muted/30 border-border/30"
                      data-testid="embed-brand-foreground"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1" data-testid="embed-brand-save-preset">
                  <Input
                    value={newPresetName}
                    onChange={e => setNewPresetName(e.target.value)}
                    placeholder="Name this palette (e.g. Acme Blue)"
                    className="h-8 text-[11px] rounded-lg bg-muted/30 border-border/30"
                    data-testid="embed-brand-preset-name"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleSavePreset}
                    disabled={savingPreset || !newPresetName.trim()}
                    className="h-8 gap-1.5 flex-shrink-0"
                    data-testid="embed-brand-preset-save-btn"
                  >
                    {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save preset
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={generating || !form.resource_id}
              className="w-full rounded-lg"
              data-testid="generate-embed-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Code className="w-4 h-4 mr-1.5" />}
              {editingEmbedId ? 'Save Changes' : 'Generate Snippet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-3xl h-[70vh]" data-testid="embed-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" /> Embed Preview
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Preview how the embedded content will appear.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 rounded-xl border border-border/30 overflow-hidden bg-background">
            {previewUrl ? (
              <iframe src={previewUrl} className="w-full h-full border-none" title="Embed Preview" />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Preview requires a valid embed token
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
